var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
/* eslint-disable react-hooks/rules-of-hooks */
import React from 'react';
/**
 * Convenience factory function that extracts type information and encodes it for use with model
 * config and `useEventMap`. Under the hood, it returns the config that was passed in. The real
 * magic is in type extraction and encoding which reduces boilerplate.
 *
 * `createEventMap` is a function that takes an `Events` generic and will return a function that
 * takes in a config object to configure all guards and callbacks. The empty function is used because
 * Typescript does not allow partial specification of generics (either you specify all generics or
 * none of them). Since `Events` cannot be inferred, it is passed to the first function.
 *
 * @example
 * type Events = {
 *   open(data: { eventData: string }): void
 * }
 *
 * const eventMap = createEventMap<Events>()({
 *   guards: {
 *     shouldOpen: 'open'
 *   },
 *   callbacks: {
 *     onOpen: 'open'
 *   }
 * })
 *
 * @deprecated `createModelHook` uses Template Literal Types to create event map types
 */
export var createEventMap = function () { return function (config) {
    // Instruct Typescript that all valid guards and callbacks exist
    return config;
}; };
// small wrapper to get `keyof T` instead of `string | number | symbol`
var keys = function (input) { return Object.keys(input); };
/**
 * This hook creates a stable reference events object to be used in a model. The reference is stable
 * by the use of `React.Memo` and uses React Refs to make sure there are no stale closure values. It
 * takes in an event map, state, model config, and an events object. It will map over each event and
 * add guards and callbacks to the event as configured in the event map.
 *
 * @param eventMap
 * @param state
 * @param config
 * @param events
 *
 * @example
 * const useDiscloseModel = (config: ModelConfig = {}): DiscloseModel => {
 *   const events = useEventMap(eventMap, state, config, {
 *     open() {
 *       // do something
 *     }
 *   }
 * })
 * @deprecated Use `createModelHook` instead
 */
export var useEventMap = function (eventMap, state, config, events) {
    // use refs so we can memoize the returned `events` object
    var eventMapRef = React.useRef(eventMap);
    var stateRef = React.useRef(state);
    var configRef = React.useRef(config);
    var eventsRef = React.useRef(events);
    // update all the refs with current values
    eventMapRef.current = eventMap;
    stateRef.current = state;
    configRef.current = config;
    eventsRef.current = events;
    return React.useMemo(function () {
        return keys(eventsRef.current).reduce(function (result, key) {
            result[key] = (function (data) {
                var _a, _b;
                // Invoke the configured guard if there is one
                if (!eventsRef.current[key]._wrapped) {
                    var guardFn = keys(eventMapRef.current.guards || {}).find(function (k) {
                        return (eventMapRef.current.guards || {})[k] === key;
                    });
                    if (guardFn && ((_a = configRef.current) === null || _a === void 0 ? void 0 : _a[guardFn]) &&
                        //@ts-ignore Typescript doesn't like that the call signatures are different
                        !configRef.current[guardFn]({ data: data, state: stateRef.current })) {
                        return;
                    }
                }
                // call the event (setter)
                eventsRef.current[key](data);
                if (!eventsRef.current[key]._wrapped) {
                    // Invoke the configured callback if there is one
                    var callbackFn = keys(eventMapRef.current.callbacks || {}).find(function (k) {
                        return (eventMapRef.current.callbacks || {})[k] === key;
                    });
                    if (callbackFn && ((_b = configRef.current) === null || _b === void 0 ? void 0 : _b[callbackFn])) {
                        //@ts-ignore Typescript doesn't like that the call signatures are different
                        configRef.current[callbackFn]({ data: data, prevState: stateRef.current });
                    }
                }
            }); // this cast keeps Typescript happy
            // Mark this function has been wrapped so we can detect wrapped events and not call guards and callbacks multiple times
            result[key]._wrapped = true;
            return result;
        }, {});
    }, []);
};
// use this?
export var createEvents = function (events) { return function (config) {
    return { events: events, eventMap: config };
}; };
function capitalize(string) {
    return string[0].toUpperCase() + string.substring(1);
}
function getGuardName(eventName) {
    return "should" + capitalize(eventName);
}
function getCallbackName(eventName) {
    return "on" + capitalize(eventName);
}
// Merges config together in a way that is type safe and works at runtime with correct rules and order applied
// to guards and callbacks.
function mergeConfig(sourceConfig, newConfig) {
    var result = __assign({}, sourceConfig);
    var _loop_1 = function (key) {
        if (typeof newConfig[key] === "function" &&
            /(on)[A-Z]/.test(key) &&
            typeof sourceConfig[key] === "function") {
            // merge callbacks and ignore Typescript's errors. We've already tested call signatures
            //  @ts-ignore
            result[key] = function (data, state) {
                // @ts-ignore
                sourceConfig[key](data, state);
                // @ts-ignore
                newConfig[key](data, state);
            };
        }
        else if (newConfig[key] &&
            /(should)[A-Z]/.test(key) &&
            sourceConfig[key]) {
            // merge guards and ignore Typescript's errors. We've already tested call signatures
            // @ts-ignore
            result[key] = function (data, state) {
                // @ts-ignore
                return sourceConfig[key](data, state) && newConfig[key](data, state);
            };
        }
        else {
            // @ts-ignore
            result[key] = newConfig[key];
        }
    };
    for (var key in newConfig) {
        _loop_1(key);
    }
    return result;
}
/**
 * Factory function to create typed models with minimal Typescript required. It is a function that
 * takes `defaultConfig` and `requiredConfig` and returns a function that will become the definition
 * of the model. The config objects will be used to generate a `getElemProps` function that is used
 * to separate model config an element props.
 *
 * Typescript will infer all config from the returned `state`, `events`, and the `defaultConfig` and
 * the `requiredConfig`. `defaultConfig` serves the additional purpose of being a default value if
 * no config value was provided. `requiredConfig` is a little odd in that config values are ignored
 * and only used to extract types.
 *
 * @example
 * const useModel = createModelHook({
 *   defaultConfig: {
 *     optional: 'right' as 'left' | 'right', // optional type casting
 *   },
 *   requiredConfig: {
 *     size: 1, // values are only used for types and are ignored at runtime
 *   }
 * })(config => {
 *   // config is pre-typed for you based on `defaultConfig`
 * })
 */
export var createModelHook = function (options) {
    var _a = options.defaultConfig, defaultConfig = _a === void 0 ? {} : _a, _b = options.requiredConfig, requiredConfig = _b === void 0 ? {} : _b, defaultContext = options.defaultContext, contextOverride = options.contextOverride;
    // create a bunch of refs so we can define the `wrappedModel` function once.
    var fnRef = { current: null };
    var callbacksRef = { current: [] };
    var guardsRef = { current: [] };
    var Context = contextOverride || React.createContext(defaultContext || { state: {}, events: {} });
    var getElemProps = function (props) {
        // if (eventsRef.current === null) {
        //   throw Error(
        //     `useModel.getElemProps() must be called after useModel(). getElemProps needs to determine the events returned by the model to function correctly.\nExample:\nconst model = useModel();\nconst elemProps = useModel.getElemProps(props);`
        //   );
        // }
        var elemProps = {};
        for (var key in props) {
            if (!defaultConfig.hasOwnProperty(key) &&
                !requiredConfig.hasOwnProperty(key) &&
                !callbacksRef.current.includes(key) &&
                !guardsRef.current.includes(key)) {
                // @ts-ignore  Typescript complains about index signatures and this type is never exposed in definitions, so suppress the error
                elemProps[key] = props[key];
            }
        }
        return elemProps;
    };
    // we use `any` here because we don't know what the type is here and it is internal. No use
    // slowing down Typescript to bother type checking
    function wrappedModelHook(config) {
        var stateRef = React.useRef({});
        var configRef = React.useRef({});
        var eventsRef = React.useRef({});
        var _a = fnRef.current(__assign(__assign({}, defaultConfig), config)), state = _a.state, events = _a.events, rest = __rest(_a, ["state", "events"]);
        // update all the refs with current values
        stateRef.current = state;
        configRef.current = config || {};
        eventsRef.current = events;
        if (!callbacksRef.current.length) {
            callbacksRef.current = keys(events).map(getCallbackName);
        }
        if (!guardsRef.current.length) {
            guardsRef.current = keys(events).map(getGuardName);
        }
        var wrappedEvents = React.useMemo(function () {
            return keys(eventsRef.current).reduce(function (result, key) {
                result[key] = function (data) {
                    // Invoke the configured guard if there is one
                    if (!eventsRef.current[key]._wrapped) {
                        var guardFnName = getGuardName(key);
                        if (configRef.current[guardFnName] &&
                            !configRef.current[guardFnName](data, stateRef.current)) {
                            return;
                        }
                    }
                    // call the event (setter)
                    eventsRef.current[key](data);
                    var callbackFnName = getCallbackName(key);
                    if (!eventsRef.current[key]._wrapped) {
                        if (configRef.current[callbackFnName]) {
                            configRef.current[callbackFnName](data, stateRef.current);
                        }
                    }
                };
                // Mark this function has been wrapped so we can detect wrapped events and not call guards and callbacks multiple times
                result[key]._wrapped = true;
                return result;
            }, {});
        }, []);
        return __assign({ state: state, events: wrappedEvents }, rest);
    }
    wrappedModelHook.getElemProps = getElemProps;
    wrappedModelHook.defaultConfig = defaultConfig;
    wrappedModelHook.requiredConfig = requiredConfig;
    wrappedModelHook.mergeConfig = mergeConfig;
    wrappedModelHook.Context = Context;
    return function (fn) {
        fnRef.current = fn;
        return wrappedModelHook; // Typescript complains about the `fn` not matching the signature. It's okay Typescript. It works at runtime
    };
};
